/**
 * EnterpriseTalentSuggestions — Round 78 (A-C-09)
 *
 * Cross-location fit suggestions surface. Strictly advisory:
 *   - deterministic boolean signals (no composite score)
 *   - server-built reason summaries (no probabilistic language)
 *   - no action buttons (no assign / offer / schedule)
 *
 * Gated by `canViewInScopeTalent`. Independent from Connect, scheduling,
 * Pensieve, talent analytics, training effectiveness.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Sparkles,
  ShieldAlert,
  Building2,
  Gauge,
  RotateCcw,
  Stethoscope,
  Layers,
  GraduationCap,
  Users,
} from 'lucide-react';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useFlooActiveScopeContext } from '@/hooks/useFlooActiveScopeContext';
import { ActiveScopeBadge } from '@/components/floo-network/ActiveScopeBadge';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import {
  useScopeFitSuggestions,
  type FitConfidence,
  type FitSignalKey,
  type ScopeFitSuggestion,
} from '@/hooks/useScopeFitSuggestions';
import type { InScopeScopeType } from '@/hooks/useInScopeTalent';

const ROLE_LABELS: Record<string, string> = {
  MANAGER: 'Manager',
  OWNER: 'Owner',
  ORG_ADMIN: 'Org Admin',
  TECHNICIAN: 'Technician',
  ADVISOR: 'Service Advisor',
  STUDENT: 'Student',
};

const SIGNAL_META: Record<FitSignalKey, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  high_efficiency: { label: 'High Efficiency', icon: Gauge },
  low_comeback_rate: { label: 'Low Comebacks', icon: RotateCcw },
  strong_diagnostic_accuracy: { label: 'Strong Diagnostics', icon: Stethoscope },
  high_job_volume: { label: 'High Job Volume', icon: Layers },
  recent_training_activity: { label: 'Recent Training', icon: GraduationCap },
};

const ALL_SIGNALS: FitSignalKey[] = [
  'high_efficiency',
  'low_comeback_rate',
  'strong_diagnostic_accuracy',
  'high_job_volume',
  'recent_training_activity',
];

const CONFIDENCE_STYLES: Record<FitConfidence, string> = {
  low: 'bg-muted text-muted-foreground border-0',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-0',
  high: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-0',
};

export default function EnterpriseTalentSuggestions() {
  const navigate = useNavigate();
  const { canViewInScopeTalent, scopeLabel } = useEnterpriseScope();
  // Phase 8 — active scope when chosen; membership fallback otherwise.
  const activeCtx = useFlooActiveScopeContext();
  const scopeType = activeCtx.scopeType;
  const scopeId = activeCtx.scopeId;

  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');
  const [requiredSignals, setRequiredSignals] = useState<Set<FitSignalKey>>(new Set());

  const filters = useMemo(() => {
    const f: Parameters<typeof useScopeFitSuggestions>[0]['filters'] = {};
    if (shopFilter !== 'all') f.shop_ids = [shopFilter];
    if (roleFilter !== 'all') f.role = roleFilter;
    if (search.trim()) f.search = search.trim();
    if (confidenceFilter !== 'all') f.confidence = confidenceFilter as FitConfidence;
    if (requiredSignals.size > 0) f.signals = Array.from(requiredSignals);
    return f;
  }, [shopFilter, roleFilter, search, confidenceFilter, requiredSignals]);

  const { suggestions, isLoading, error } = useScopeFitSuggestions({
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
    filters,
  });

  // Build filter option lists from full result set (pre-filtering would empty them)
  const { suggestions: allForOptions } = useScopeFitSuggestions({
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
  });

  const shops = useMemo(() => {
    const m = new Map<string, string>();
    allForOptions.forEach((r) => m.set(r.current_shop_id, r.current_shop_name));
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [allForOptions]);

  const roles = useMemo(() => {
    const s = new Set<string>();
    allForOptions.forEach((r) => s.add(r.role));
    return Array.from(s).sort();
  }, [allForOptions]);

  const toggleSignal = (k: FitSignalKey) => {
    setRequiredSignals((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  if (!canViewInScopeTalent) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Access Restricted</CardTitle>
            </div>
            <CardDescription>
              Fit suggestions are available to managers, owners, and org admins only.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-4">
      <BackToOptiNetworkButton testId="enterprise-talent-suggestions-back-to-opti-network" />
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold">{scopeLabel} Fit Suggestions</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Technicians who appear to show strong patterns within your scope.
          Advisory only — based on observable performance, not predictions.
        </p>
        {/* Phase 8 — active scope indicator. */}
        <ActiveScopeBadge className="mt-2" testId="floo-active-scope-badge-talent-suggestions" />
      </div>

      <Tabs value="suggestions" className="w-full">
        <TabsList>
          <TabsTrigger value="directory" onClick={() => navigate('/enterprise/talent')}>
            Directory
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            onClick={() => navigate('/enterprise/talent/analytics')}
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger value="training" onClick={() => navigate('/enterprise/training')}>
            Training
          </TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Search name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="sm:max-w-xs"
              />
              <Select value={shopFilter} onValueChange={setShopFilter}>
                <SelectTrigger className="sm:w-[200px]">
                  <SelectValue placeholder="All shops" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All shops</SelectItem>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="sm:w-[180px]">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r] ?? r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
                <SelectTrigger className="sm:w-[160px]">
                  <SelectValue placeholder="All confidence" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All confidence</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground self-center">Require signals:</span>
              {ALL_SIGNALS.map((k) => {
                const active = requiredSignals.has(k);
                const Icon = SIGNAL_META[k].icon;
                return (
                  <Button
                    key={k}
                    type="button"
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    onClick={() => toggleSignal(k)}
                    className="h-7 text-xs gap-1"
                  >
                    <Icon className="h-3 w-3" />
                    {SIGNAL_META[k].label}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-6 text-center">
              {error.message}
            </p>
          ) : suggestions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="h-8 w-8 mb-2" />
              <p className="text-sm max-w-md text-center">
                No fit suggestions yet — needs at least 10 completed jobs and 2 signal
                matches per technician.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {suggestions.map((s) => (
                <SuggestionCard key={`${s.user_id}-${s.current_shop_id}`} s={s} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SuggestionCard({ s }: { s: ScopeFitSuggestion }) {
  const trueSignals = (Object.entries(s.fit_signals) as [FitSignalKey, boolean][])
    .filter(([, v]) => v)
    .map(([k]) => k);

  return (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link
              to={`/enterprise/talent/${s.user_id}?mode=in_scope`}
              className="text-primary font-semibold hover:underline truncate block"
            >
              {s.display_name}
            </Link>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{s.current_shop_name}</span>
            </div>
          </div>
          <Badge className={`${CONFIDENCE_STYLES[s.confidence_level]} text-xs capitalize shrink-0`}>
            {s.confidence_level} confidence
          </Badge>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline" className="text-xs">
            {ROLE_LABELS[s.role] ?? s.role}
          </Badge>
          {s.technician_type_title && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              {s.technician_type_title}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {trueSignals.map((k) => {
            const Icon = SIGNAL_META[k].icon;
            return (
              <Badge
                key={k}
                variant="secondary"
                className="text-xs gap-1 bg-primary/10 text-primary hover:bg-primary/15"
              >
                <Icon className="h-3 w-3" />
                {SIGNAL_META[k].label}
              </Badge>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          {s.reason_summary}
        </p>

        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
          <span>{s.jobs_completed.toLocaleString()} jobs</span>
          {s.diagnostic_sample_size > 0 && (
            <span>· {s.diagnostic_sample_size} diagnostic outcomes</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
